import React, { useState, useEffect } from 'react';
import ItemCard from '@/components/ItemCard';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import usePageTitle from '@/hooks/usePageTitle';

const PlatformsIndex = () => {
  // Set page title
  usePageTitle('Platforms');
  
  const { toast } = useToast();
  const [platforms, setPlatforms] = useState<{id: string, name: string, url_slug: string}[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPlatforms = async () => {
      try {
        const { data, error } = await supabase
          .from('platforms')
          .select('id, name, url_slug')
          .order('name');
        
        if (error) throw error;
        
        if (data) {
          setPlatforms(data);
        }
      } catch (error) {
        console.error('Error fetching platforms:', error);
        toast({
          title: "Error loading platforms",
          description: "There was a problem loading the platforms. Please refresh the page.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchPlatforms();
  }, [toast]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navigation />
      
      <main className="container mx-auto px-4 py-8 flex-grow">
        <div className="mb-8">
          <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-4">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Home
          </Link>
          
          <h1 className="text-3xl font-bold mb-2">Browse by Platform</h1>
          <p className="text-gray-600">
            Find fitness apps for your preferred platform.
          </p>
        </div>
        
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <p className="text-gray-500">Loading platforms...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {platforms.length > 0 ? (
              platforms.map((platform) => (
                <ItemCard
                  key={platform.id}
                  name={platform.name}
                  slug={platform.url_slug}
                  type="platform"
                />
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <p className="text-gray-500">No platforms available.</p>
              </div>
            )}
          </div>
        )}
      </main>
      
      <Footer />
    </div>
  );
};

export default PlatformsIndex;
